/**
 * AuthClientContext.tsx
 * ─────────────────────
 * Runs the session bootstrap sequence on mount and restores it after
 * iPad/PWA resume, then exposes
 * loginWithRedirect / logout helpers (Auth0 SDK).
 *
 * Bootstrap priority:
 *   1. localStorage device token  →  POST /api/devices/auth
 *   2. refreshToken cookie         →  GET  /api/auth/session  (rotates cookie)
 *   3. Auth0 silent token          →  POST /api/auth/session  (exchange + issues cookie)
 *   4. None of the above           →  mode = "none"
 *
 * Auth0 is ONLY used for:
 *   • loginWithRedirect()  (step 3 fallback + explicit login button)
 *   • logout()             (clears Auth0 session)
 *   • getAccessTokenSilently() for one-time exchange (step 3)
 */

import React, { createContext, useContext, useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  consumeExplicitLogoutHandoff,
  setSession,
  getSession,
  deriveRoles,
  type SessionUser,
} from "@/lib/sessionStore";

const AUTH_AUDIENCE =
  (window as any).__ENV__?.AUTH0_AUDIENCE || "https://showme.app/api";

type AuthClientCtx = { ready: boolean };
const AuthClientContext = createContext<AuthClientCtx | null>(null);

// ── Module-level refs for non-React access ───────────────────────────────────
let _loginRedirect: (opts?: any) => Promise<void> = async () => {};
let _auth0Logout: (opts?: any) => void = () => {};
let _getAuth0Token: () => Promise<string | null> = async () => null;
let _resumeBootstrapInFlight: Promise<boolean> | null = null;

const SERVER_ACCESS_TOKEN_TTL_MS = 15 * 60 * 1000;

export function getLoginRedirect() { return _loginRedirect; }
export function getAuth0Logout()   { return _auth0Logout; }

function getSafeAuth0Error(error: unknown): {
  code: string | null;
  name: string | null;
  status: string | number | null;
  descriptionPresent: boolean;
} {
  if (!error || typeof error !== "object") {
    return { code: null, name: null, status: null, descriptionPresent: false };
  }

  const value = error as Record<string, unknown>;
  const code =
    typeof value.error === "string"
      ? value.error
      : typeof value.code === "string"
        ? value.code
        : null;
  const name = typeof value.name === "string" ? value.name : null;
  const status =
    typeof value.status === "number" || typeof value.status === "string"
      ? value.status
      : null;
  const descriptionPresent =
    typeof value.error_description === "string" ||
    typeof value.message === "string";

  return { code, name, status, descriptionPresent };
}

function logAuth0CacheMetadata(stage: string): void {
  try {
    const entries: Array<Record<string, unknown>> = [];

    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key || (!key.includes("@@auth0spajs@@") && !key.toLowerCase().includes("auth0"))) {
        continue;
      }

      const raw = localStorage.getItem(key);
      let parsed: unknown = null;
      try {
        parsed = raw ? JSON.parse(raw) : null;
      } catch {
        parsed = null;
      }

      const record =
        parsed && typeof parsed === "object"
          ? parsed as Record<string, unknown>
          : {};
      const body =
        record.body && typeof record.body === "object"
          ? record.body as Record<string, unknown>
          : record;

      entries.push({
        storageKeyLength: key.length,
        valueType: raw === null ? "missing" : "json",
        hasAccessToken: typeof body.access_token === "string" || typeof body.accessToken === "string",
        hasRefreshToken: typeof body.refresh_token === "string" || typeof body.refreshToken === "string",
        hasIdToken: typeof body.id_token === "string" || typeof body.idToken === "string",
        expiresAt: typeof body.expiresAt === "number" ? body.expiresAt : null,
        scope: typeof body.scope === "string" ? body.scope : null,
        audiencePresent: typeof body.audience === "string" && body.audience.length > 0,
      });
    }

    console.log(`[AuthDiag] Auth0 localStorage metadata stage=${stage}`, {
      matchingEntryCount: entries.length,
      entries,
      tokenValuesIncluded: false,
    });
  } catch (error) {
    console.warn(`[AuthDiag] Auth0 localStorage metadata unavailable stage=${stage}`, {
      errorName: error instanceof Error ? error.name : typeof error,
      tokenValuesIncluded: false,
    });
  }
}

// ── Bootstrap helpers ─────────────────────────────────────────────────────────

async function bootstrapDeviceToken(): Promise<boolean> {
  const raw = localStorage.getItem("showme_device_token");
  if (!raw) return false;

  try {
    const res = await fetch("/api/devices/auth", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceToken: raw }),
    });

    if (!res.ok) {
      if (res.status === 401) {
        // Revoked — clean up
        localStorage.removeItem("showme_device_token");
      }
      return false;
    }

    const data = await res.json();
    if (data.accessToken && data.user) {
      setSession({
        mode: "device",
        accessToken: data.accessToken,
        user: {
          ...data.user,
          roles: data.user.roles ?? [],
          permissions: data.user.permissions ?? [],
        } as SessionUser,
        initialized: true,
      });
      console.log("[AuthClient] Bootstrap: device mode →", data.user?.deviceName);
      return true;
    }
  } catch (err) {
    console.warn("[AuthClient] Device token bootstrap error:", err);
  }
  return false;
}

async function bootstrapCookieSession(
  canApplySession: () => boolean = () => true,
  context: "cold-start" | "resume" = "cold-start",
): Promise<boolean> {
  try {
    console.log(`[AuthDiag] GET /api/auth/session start context=${context} credentials=include`);
    const res = await fetch("/api/auth/session", {
      method: "GET",
      credentials: "include",
    });

    console.log(`[AuthDiag] GET /api/auth/session result context=${context} status=${res.status} ok=${res.ok}`);
    if (!res.ok) {
      const safeBody = await res.clone().json().catch(() => ({}));
      console.warn(`[AuthDiag] Cookie session restoration failed context=${context}`, {
        status: res.status,
        error: typeof safeBody?.error === "string" ? safeBody.error : null,
        authMode: typeof safeBody?.authMode === "string" ? safeBody.authMode : null,
      });
      return false;
    }

    const data = await res.json();
    if (data.accessToken && data.user) {
      if (!canApplySession()) {
        console.log("[AuthClient] Cookie bootstrap result ignored because session changed");
        return false;
      }

      setSession({
        mode: "server",
        accessToken: data.accessToken,
        user: {
          ...data.user,
          roles: data.user.roles ?? deriveRoles(undefined, data.user.subscription),
          permissions: data.user.permissions ?? [],
        } as SessionUser,
        initialized: true,
      });
      console.log("[AuthClient] Bootstrap: server mode (cookie) →", data.user?.email);
      console.log(`[AuthDiag] Cookie session restoration succeeded context=${context} accessTokenPresent=true`);
      return true;
    }

    console.warn(`[AuthDiag] Cookie session response lacked session data context=${context}`, {
      accessTokenPresent: Boolean(data.accessToken),
      userPresent: Boolean(data.user),
    });
  } catch (err) {
    console.warn("[AuthClient] Cookie bootstrap error:", err);
    console.warn(`[AuthDiag] Cookie session request threw context=${context}`, {
      errorName: err instanceof Error ? err.name : typeof err,
    });
  }
  return false;
}

async function bootstrapAuth0Exchange(
  canApplySession: () => boolean = () => true,
  context: "cold-start" | "resume" = "cold-start",
): Promise<boolean> {
  try {
    console.log(`[AuthDiag] getAccessTokenSilently start context=${context}`);
    const auth0Token = await _getAuth0Token();
    console.log(`[AuthDiag] getAccessTokenSilently result context=${context} tokenPresent=${Boolean(auth0Token)}`);
    if (!auth0Token) return false;

    console.log(`[AuthDiag] POST /api/auth/session start context=${context} bearerPresent=true`);
    const res = await fetch("/api/auth/session", {
      method: "POST",
      credentials: "include",
      headers: { Authorization: `Bearer ${auth0Token}` },
    });

    console.log(`[AuthDiag] POST /api/auth/session result context=${context} status=${res.status} ok=${res.ok}`);
    if (!res.ok) {
      const safeBody = await res.clone().json().catch(() => ({}));
      console.warn(`[AuthDiag] Auth0 server exchange failed context=${context}`, {
        status: res.status,
        error: typeof safeBody?.error === "string" ? safeBody.error : null,
      });
      return false;
    }

    const data = await res.json();
    if (data.accessToken && data.user) {
      if (!canApplySession()) {
        console.log("[AuthClient] Auth0 exchange result ignored because session changed");
        return false;
      }

      setSession({
        mode: "server",
        accessToken: data.accessToken,
        user: {
          ...data.user,
          roles: data.user.roles ?? [],
          permissions: data.user.permissions ?? [],
        } as SessionUser,
        initialized: true,
      });
      console.log("[AuthClient] Bootstrap: server mode (Auth0 exchange) →", data.user?.email);
      console.log(`[AuthDiag] Auth0 server exchange succeeded context=${context} accessTokenPresent=true`);
      return true;
    }

    console.warn(`[AuthDiag] Auth0 server exchange response lacked session data context=${context}`, {
      accessTokenPresent: Boolean(data.accessToken),
      userPresent: Boolean(data.user),
    });
  } catch (err) {
    console.warn("[AuthClient] Auth0 exchange bootstrap error:", err);
    console.warn(`[AuthDiag] Auth0 server exchange threw context=${context}`, {
      errorName: err instanceof Error ? err.name : typeof err,
    });
  }
  return false;
}

/**
 * Restore an existing authenticated server session after the app returns from
 * the background. Unlike initial bootstrap, a resume failure must preserve the
 * current session because it may only be a transient connectivity problem.
 */
async function bootstrapResumeSession(canApplySession: () => boolean): Promise<boolean> {
  if (_resumeBootstrapInFlight) {
    console.log("[AuthClient] Resume restoration skipped because another restoration is already in flight");
    return _resumeBootstrapInFlight;
  }

  _resumeBootstrapInFlight = (async () => {
    try {
      console.log("[AuthClient] Resume restoration attempted");

      if (await bootstrapCookieSession(canApplySession, "resume")) {
        console.log("[AuthClient] Resume cookie restoration succeeded");
        return true;
      }
      console.log("[AuthClient] Resume cookie restoration failed; attempting Auth0 fallback");

      if (await bootstrapAuth0Exchange(canApplySession, "resume")) {
        console.log("[AuthClient] Resume Auth0 fallback succeeded");
        return true;
      }

      console.warn("[AuthClient] Resume restoration failed; preserving current authenticated session");
      return false;
    } finally {
      _resumeBootstrapInFlight = null;
    }
  })();

  return _resumeBootstrapInFlight;
}

// ── Provider ─────────────────────────────────────────────────────────────────

export const AuthClientProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getAccessTokenSilently, isLoading, loginWithRedirect, logout } = useAuth0();
  const bootstrapRan = useRef(false);
  const hiddenAtRef = useRef<number | null>(null);

  // Wire up module-level refs immediately (stable across re-renders)
  useEffect(() => {
    _auth0Logout = logout;

    _loginRedirect = async (opts?: any) => {
      try {
        await loginWithRedirect({
          authorizationParams: {
            audience: AUTH_AUDIENCE,
            scope: "openid profile email",
            redirect_uri:
              (window as any).__ENV__?.VITE_PUBLIC_BASE_URL
                ? `${(window as any).__ENV__?.VITE_PUBLIC_BASE_URL}/auth0/callback`
                : `${window.location.origin}/auth0/callback`,
            ...(opts || {}),
          },
          appState: { returnTo: window.location.pathname },
        });
      } catch (err) {
        console.error("[AuthClient] loginWithRedirect failed:", err);
      }
    };

    _getAuth0Token = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: AUTH_AUDIENCE, scope: "openid profile email" },
        });
        console.log("[AuthDiag] getAccessTokenSilently completed successfully", {
          tokenPresent: Boolean(token),
        });
        return token;
      } catch (error) {
        console.warn("[AuthDiag] getAccessTokenSilently failed", getSafeAuth0Error(error));
        logAuth0CacheMetadata("after-silent-token-failure");
        return null;
      }
    };
  }, [getAccessTokenSilently, loginWithRedirect, logout]);

  // Run bootstrap once (after Auth0 SDK is no longer loading)
  useEffect(() => {
    if (isLoading || bootstrapRan.current) return;
    bootstrapRan.current = true;

    (async () => {
      // Care View is a public, independently PIN-protected experience.
      // Do not probe the authenticated app session on a shared deep link:
      // a stale app session cookie must not delay or break Care View startup.
      if (window.location.pathname === "/care-view") {
        setSession({ mode: "none", user: null, accessToken: null, initialized: true });
        console.log("[AuthClient] Bootstrap skipped for public Care View");
        return;
      }

      if (consumeExplicitLogoutHandoff()) {
        setSession({ mode: "none", user: null, accessToken: null, initialized: true });
        console.log("[AuthClient] Bootstrap skipped for explicit logout handoff");
        return;
      }

      logAuth0CacheMetadata("cold-start-before-bootstrap");

      // Priority 1: device token
      if (await bootstrapDeviceToken()) return;

      // Priority 2: refresh cookie / server JWT
      if (await bootstrapCookieSession(undefined, "cold-start")) {
        // Cookie bootstrap may succeed but return permissions: [] when
        // account_role hasn't been written to 'admin' in the DB yet.
        // This happens when the cached refreshToken was issued before the
        // POST /api/auth/session sync logic ran (e.g. stale Playwright
        // .auth/admin.json). If the session store shows no permissions,
        // attempt an Auth0 silent token exchange so that:
        //  a) POST /api/auth/session fires and syncs account_role in the DB
        //  b) the session store is upgraded with RBAC permissions from
        //     the Auth0 JWT (which may already have access:admin-page)
        // The Auth0 SDK finds the locally-cached token from localStorage,
        // so this works even without a network round-trip to Auth0.
        const currentPerms = getSession().user?.permissions ?? [];
        if (currentPerms.length === 0) {
          console.log('[AuthClient] Cookie session has no permissions — attempting Auth0 upgrade');
          await bootstrapAuth0Exchange(undefined, "cold-start");
        }
        return;
      }

      // Priority 3: Auth0 silent token → exchange for server session
      if (await bootstrapAuth0Exchange(undefined, "cold-start")) return;

      // None worked — mark initialized so the app can show the login screen
      setSession({ mode: "none", user: null, accessToken: null, initialized: true });
      console.log("[AuthClient] Bootstrap: no session found");
      logAuth0CacheMetadata("cold-start-after-bootstrap-failure");
    })();
  }, [isLoading]);

  // Restore an existing authenticated server session after iPad/PWA resume.
  // The 401 refresh path remains the fallback for requests made before this
  // handler runs or when the network is temporarily unavailable.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        if (hiddenAtRef.current === null) hiddenAtRef.current = Date.now();
        return;
      }

      if (document.visibilityState === "visible") {
        void attemptResumeRestore("visibilitychange", false);
      }
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      void attemptResumeRestore("pageshow", event.persisted);
    };

    const attemptResumeRestore = async (trigger: "pageshow" | "visibilitychange", persisted: boolean) => {
      if (window.location.pathname === "/care-view") {
        console.log(`[AuthClient] Resume restoration skipped because Care View is active (${trigger})`);
        return;
      }

      const session = getSession();
      if (!session.initialized || session.mode !== "server") {
        console.log(`[AuthClient] Resume restoration skipped because authentication is not in a server session (${trigger})`);
        return;
      }

      const hiddenForMs = hiddenAtRef.current === null ? 0 : Date.now() - hiddenAtRef.current;
      const needsRestore =
        !session.accessToken ||
        hiddenForMs >= SERVER_ACCESS_TOKEN_TTL_MS ||
        persisted;

      if (!needsRestore) {
        console.log(`[AuthClient] Resume restoration skipped because authentication does not require restoration (${trigger})`);
        return;
      }

      const sessionAtStart = session;
      const canApplySession = () => {
        const current = getSession();
        return (
          current.initialized &&
          current.mode === "server" &&
          current.user?.id === sessionAtStart.user?.id
        );
      };

      const restored = await bootstrapResumeSession(canApplySession);
      if (restored) hiddenAtRef.current = null;
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  return (
    <AuthClientContext.Provider value={{ ready: !isLoading }}>
      {children}
    </AuthClientContext.Provider>
  );
};

// ── Hooks & exports ───────────────────────────────────────────────────────────

export function useAuthClient() {
  const ctx = useContext(AuthClientContext);
  if (!ctx) throw new Error("useAuthClient must be used within AuthClientProvider");
  return ctx;
}

/**
 * Legacy non-hook token getter used by queryClient / old code.
 * Returns the server access token from the session store.
 */
export async function getAccessToken(): Promise<string | null> {
  const { getSession } = await import("@/lib/sessionStore");
  return getSession().accessToken;
}

/** Clear server session (fires and forgets logout API call) */
export function clearServerSession(): void {
  fetch("/api/auth/logout", { method: "POST", credentials: "include" }).catch(() => {});
  setSession({ mode: "none", user: null, accessToken: null, initialized: true });
}

/** Trigger Auth0 login redirect */
export async function ensureAccessToken(): Promise<string | null> {
  const { getSession } = await import("@/lib/sessionStore");
  const tok = getSession().accessToken;
  if (tok) return tok;
  await _loginRedirect({});
  return null;
}
